'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * Rascunho de formulário que sobrevive a sair da tela.
 *
 * Existe para o caso do prontuário: o profissional está escrevendo uma evolução,
 * precisa agendar um exame para o mesmo paciente, sai — e ao voltar encontrava o
 * formulário em branco. Texto clínico perdido é retrabalho e, pior, é informação que
 * o profissional já não repete igual.
 *
 * `sessionStorage` (não `localStorage`) de propósito: o rascunho é da ABA e da
 * sessão. Um rascunho de semanas atrás reaparecendo num prontuário é pior que nenhum.
 */

/** Rascunho velho não volta: passou disso, foi abandonado mesmo. */
const TTL_MS = 12 * 60 * 60 * 1000

interface StoredDraft<T> {
    value: T
    at: number
}

export interface FormDraftControls {
    /** Há um rascunho recuperado na tela agora. */
    restored: boolean
    /** Quando ele foi salvo, para a tela dizer ao usuário. */
    savedAt: Date | null
    /** Volta ao conteúdo original e apaga o rascunho. */
    discard: () => void
    /** Apaga sem restaurar nada — usar após salvar de verdade. */
    clear: () => void
}

/**
 * @param key      Identifica o formulário E o registro (ex.: `evolucao:123:nova`).
 *                 Chave repetida entre registros faria o rascunho de um paciente
 *                 aparecer no de outro.
 * @param value    Estado atual do formulário.
 * @param onRestore Chamado UMA vez, com o rascunho encontrado.
 * @param enabled  Só começa a gravar quando a tela terminou de carregar — senão o
 *                 estado inicial vazio sobrescreve o rascunho antes de restaurá-lo.
 */
export function useFormDraft<T>(
    key: string,
    value: T,
    onRestore: (draft: T) => void,
    enabled: boolean,
): FormDraftControls {
    const storageKey = `form-draft:${key}`
    const [restored, setRestored] = useState(false)
    const [savedAt, setSavedAt] = useState<Date | null>(null)
    const originalRef = useRef<T | null>(null)
    const checkedRef = useRef(false)

    // Restauração: uma única vez, e só depois que a tela carregou o registro.
    useEffect(() => {
        if (!enabled || checkedRef.current) return
        checkedRef.current = true
        originalRef.current = value
        try {
            const raw = sessionStorage.getItem(storageKey)
            if (!raw) return
            const draft = JSON.parse(raw) as StoredDraft<T>
            if (!draft?.at || Date.now() - draft.at > TTL_MS) {
                sessionStorage.removeItem(storageKey)
                return
            }
            onRestore(draft.value)
            setRestored(true)
            setSavedAt(new Date(draft.at))
        } catch {
            // Storage indisponível: o formulário funciona, só não guarda rascunho.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled])

    // Gravação: a cada mudança, com respiro para não escrever a cada tecla.
    useEffect(() => {
        if (!enabled || !checkedRef.current) return
        const timer = setTimeout(() => {
            try {
                const payload: StoredDraft<T> = { value, at: Date.now() }
                sessionStorage.setItem(storageKey, JSON.stringify(payload))
            } catch {
                // Cota estourada ou storage bloqueado: seguir sem rascunho.
            }
        }, 800)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, enabled])

    const clear = () => {
        try {
            sessionStorage.removeItem(storageKey)
        } catch {
            // Nada a fazer.
        }
        setRestored(false)
    }

    const discard = () => {
        clear()
        if (originalRef.current !== null) onRestore(originalRef.current)
    }

    return { restored, savedAt, discard, clear }
}
